/**
 * useSystemSettings Hook
 *
 * Firestore'dan sistem ayarlarını (kill switch flag'leri) okur
 * ve bakım modu durumunu kontrol eder.
 *
 * Kullanım:
 * const { bakim, ozellikler, loading } = useSystemSettings();
 *
 * if (bakim.aktif) {
 *   // Bakım modu mesajını göster
 * }
 */

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Sistem Ayarları Interface
 */
export interface SistemAyarlari {
  ozellikler: {
    hesapOlusturma: boolean;
    sohbetOlusturma: boolean;
    aracYonetimi: boolean;
    aiIstekleri: boolean;
    mesajGonderme: boolean;
    supportpage: boolean;
    sanayiRaporu: boolean;
  };
  bakim: {
    aktif: boolean;
    mesaj: string;
  };
  guncellemeTarihi?: string;
}

/**
 * Default ayarlar (Firestore'da doküman yoksa)
 */
const DEFAULT_SETTINGS: SistemAyarlari = {
  ozellikler: {
    hesapOlusturma: true,
    sohbetOlusturma: true,
    aracYonetimi: true,
    aiIstekleri: true,
    mesajGonderme: true,
    supportpage: false,
    sanayiRaporu: true,
  },
  bakim: {
    aktif: false,
    mesaj: '',
  },
};

/**
 * Sistem ayarlarını dinleyen hook
 */
export function useSystemSettings() {
  const [settings, setSettings] = useState<SistemAyarlari>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Firestore'dan config/sistemAyarlari dokümanını dinle
    const settingsRef = doc(db, 'config', 'sistemAyarlari');
    let unsubscribe: (() => void) | null = null;

    // Wrap in try-catch to handle permission errors gracefully during sign-out
    try {
      unsubscribe = onSnapshot(
        settingsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as SistemAyarlari;
            setSettings(data);

            // Console log (debug için)
            if (data.bakim.aktif) {
              console.warn('🚨 Bakım Modu Aktif:', data.bakim.mesaj);
            } else {
              console.log('✅ Sistem Ayarları: Tüm özellikler aktif');
            }
          } else {
            // Doküman yoksa default ayarlar kullan
            console.warn('⚠️ Sistem ayarları dokümanı bulunamadı. Default ayarlar kullanılıyor.');
            setSettings(DEFAULT_SETTINGS);
          }
          setLoading(false);
        },
        (err) => {
          // Suppress permission errors (happens during sign-out)
          const error = err as any;
          if (error.code !== 'permission-denied') {
            console.error('❌ Sistem ayarları dinlenirken hata:', err);
            setError(err as Error);
          }
          // Hata durumunda default ayarlar kullan (fail-safe)
          setSettings(DEFAULT_SETTINGS);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to setup system settings listener:', error);
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
    }

    // Cleanup
    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Suppress cleanup errors
        }
      }
    };
  }, []);

  return {
    ...settings,
    loading,
    error,
  };
}
